import { useState } from "react";
import { Bot, User, FileText, Download, Loader2 } from "lucide-react";
import { downloadDocument } from "../lib/api";

const formatBytes = (n) => {
  if (!n || !Number.isFinite(n)) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const UserUploadChip = ({ attachment }) => {
  const size = formatBytes(attachment.size);
  return (
    <div
      data-testid={`user-upload-chip-${attachment.document_id || attachment.filename}`}
      className="mt-2 inline-flex items-center gap-2 max-w-full pl-2 pr-3 py-1.5 rounded-lg bg-brand-deeper/70 border border-brand-mint/30"
    >
      <FileText className="h-3.5 w-3.5 text-brand-mint shrink-0" />
      <span className="text-xs text-brand-mint truncate max-w-[260px]">
        {attachment.filename}
        {size ? ` · ${size}` : ""}
      </span>
    </div>
  );
};

const AttachmentButton = ({ attachment }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isExternal = attachment.type === "onedrive_word_doc";

  const onClick = async () => {
    if (isExternal) {
      // OneDrive web link — open in a new tab; user is authenticated to MS in their browser.
      if (attachment.download_url) {
        window.open(attachment.download_url, "_blank", "noopener,noreferrer");
      } else {
        setError("OneDrive link not available.");
      }
      return;
    }
    setBusy(true);
    setError("");
    try {
      await downloadDocument(attachment.document_id, attachment.filename);
    } catch (_) {
      setError("Download failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const size = formatBytes(attachment.size);
  const subtitle = isExternal
    ? "Word document · OneDrive"
    : `Word document${size ? ` · ${size}` : ""}`;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        data-testid={`attachment-download-${attachment.document_id || attachment.filename}`}
        className="group inline-flex items-center gap-3 w-full md:w-auto px-3.5 py-2.5 rounded-xl bg-brand-deeper/70 hover:bg-brand-deeper/90 border border-brand-mint/25 hover:border-brand-mint/50 text-left transition-colors disabled:opacity-60"
      >
        <span className="h-9 w-9 shrink-0 rounded-lg bg-brand-mint/15 border border-brand-mint/30 flex items-center justify-center text-brand-mint">
          <FileText className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-brand-mint truncate">
            {attachment.filename}
          </span>
          <span className="block text-[11px] text-brand-mint/60">
            {subtitle}
          </span>
        </span>
        <span className="shrink-0 text-brand-mint/80 group-hover:text-brand-mintBright">
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </span>
      </button>
      {error && (
        <p className="mt-1 text-xs text-red-200">{error}</p>
      )}
    </div>
  );
};

export const ChatMessage = ({ role, content, attachments = [] }) => {
  const isUser = role === "user";

  if (isUser) {
    const userAttachments = Array.isArray(attachments) ? attachments : [];
    return (
      <div
        data-testid="chat-message-user"
        className="flex items-start justify-end gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300"
      >
        <div className="max-w-[80%] md:max-w-[70%] flex flex-col items-end">
          <div className="px-4 py-3 bg-brand-mint text-brand rounded-2xl rounded-tr-sm shadow-md shadow-brand-deeper/40">
            <p className="text-sm leading-relaxed whitespace-pre-wrap font-medium">{content}</p>
          </div>
          {userAttachments.map((a) => (
            <UserUploadChip key={a.document_id || a.filename} attachment={a} />
          ))}
        </div>
        <div className="h-8 w-8 shrink-0 rounded-full bg-brand-deep border border-brand-mint/20 flex items-center justify-center text-brand-mint">
          <User className="h-4 w-4" />
        </div>
      </div>
    );
  }

  const hasText = content && content.trim().length > 0;
  const hasAttachments = Array.isArray(attachments) && attachments.length > 0;

  return (
    <div
      data-testid="chat-message-assistant"
      className="flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300"
    >
      <div className="h-8 w-8 shrink-0 rounded-full bg-brand-mint/15 border border-brand-mint/40 flex items-center justify-center text-brand-mint">
        <Bot className="h-4 w-4" />
      </div>
      <div className="max-w-[85%] md:max-w-[75%] space-y-2">
        {hasText && (
          <div className="px-4 py-3 bg-brand-deep/70 backdrop-blur-md border border-brand-mint/20 text-brand-mint rounded-2xl rounded-tl-sm shadow-sm">
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{content}</p>
          </div>
        )}
        {hasAttachments && (
          <div data-testid="chat-message-attachments" className="space-y-2">
            {attachments.map((a) => (
              <AttachmentButton key={a.document_id} attachment={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const TypingIndicator = () => (
  <div
    data-testid="chat-typing-indicator"
    className="flex items-start gap-3 animate-in fade-in duration-200"
  >
    <div className="h-8 w-8 shrink-0 rounded-full bg-brand-mint/15 border border-brand-mint/40 flex items-center justify-center text-brand-mint">
      <Bot className="h-4 w-4" />
    </div>
    <div className="px-4 py-3 bg-brand-deep/70 backdrop-blur-md border border-brand-mint/20 rounded-2xl rounded-tl-sm">
      <div className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-brand-mint animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="h-2 w-2 rounded-full bg-brand-mint animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="h-2 w-2 rounded-full bg-brand-mint animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  </div>
);

export default ChatMessage;
